/**
 * O RECORTE — o que o painel clicou, escrito de um jeito que a lista entende.
 *
 * DEV, 04/09/2026, com dado real na tela: os números da "Situação da
 * carteira" e as barras dos dois gráficos **não levavam a lugar nenhum**. O
 * fiscal lia "11 vencendo em 90 dias" e não tinha como ver quais são.
 *
 * O recorte carrega DUAS coisas, e as duas são obrigatórias:
 *
 * 1. `parametros` — o que vai na consulta da rota `/contratos`;
 * 2. `rotulo` — a frase que a etiqueta do recorte mostra na lista, em português.
 *
 * A tarja não é enfeite. Lista filtrada sem aviso é a forma mais fácil de o
 * fiscal achar que o contrato sumiu: ele procura o 271, não acha, e conclui
 * que o sistema perdeu o registro. Por isso o rótulo viaja junto do filtro, e
 * não há como criar um recorte sem ele.
 */

export type RecorteDaLista = {
  /** A frase da etiqueta do recorte: "Vencendo em até 90 dias · contratos vigentes". */
  rotulo: string
  /** Os parâmetros de consulta da rota `/contratos`. */
  parametros: Record<string, string>
  /** Quantos a tela que abriu o recorte tinha contado. A lista compara com o
   *  que o servidor devolveu e AVISA quando os dois divergem — divergência é
   *  achado, não coisa para esconder. Nulo quando quem abriu não contou. */
  esperado?: number | null
}

export function consultaDoRecorte(recorte: RecorteDaLista | null): string {
  if (recorte === null) return ""
  const parametros = new URLSearchParams(recorte.parametros)
  return parametros.toString()
}

/**
 * OS RECORTES DO PAINEL, num lugar só.
 *
 * Cada um nasce ao lado do número que ele abre, e é por isso que eles moram
 * aqui e não espalhados pela tela: o dia em que a conta do cartão mudar, o
 * filtro que ela abre tem de mudar na mesma linha de raciocínio — e num
 * arquivo só isso se vê.
 *
 * `referencia` é a parte que não é óbvia. O cartão "Vencendo em 90 dias" conta
 * os VIGENTES pela `vigencia_fim` do cadastro (11 na PMCJ em 04/09/2026); a
 * barra "até 90 dias" conta os ATIVOS pela data da MEMÓRIA de vigência (12, no
 * mesmo dia). São duas perguntas diferentes, e um filtro só respondendo às
 * duas faria um dos números abrir uma lista com contagem diferente da que
 * estava escrita nele.
 */
export const RECORTES = {
  vigentes: (quantos: number | null): RecorteDaLista => ({
    rotulo: "Contratos vigentes",
    parametros: { situacao: "vigente" },
    esperado: quantos,
  }),

  vencendoEm: (dias: number, quantos: number | null): RecorteDaLista => ({
    rotulo: `Vigentes com término em até ${dias} dias — data do cadastro`,
    parametros: { situacao: "vigente", vencendo_em: String(dias) },
    esperado: quantos,
  }),

  vencendoNaFaixa: (dias: number, quantos: number | null): RecorteDaLista => ({
    rotulo: `A vencer em até ${dias} dias — data da memória de vigência`,
    parametros: {
      ativos: "true",
      referencia: "memoria",
      vencendo_em: String(dias),
    },
    esperado: quantos,
  }),

  // O rótulo repete o texto do nível no Painel ("datas expressas no contrato"),
  // com a inicial maiúscula, e não o nome interno "nível A" (08/10/2026).
  porNivel: (nivel: string, quantos: number | null, emPalavras: string): RecorteDaLista => ({
    rotulo: emPalavras.replace(/^./, (letra) => letra.toLocaleUpperCase("pt-BR")),
    parametros: { ativos: "true", nivel },
    esperado: quantos,
  }),

  semFiscal: (quantos: number | null): RecorteDaLista => ({
    rotulo: "Sem fiscal designado — entre os contratos não encerrados",
    parametros: { sem_fiscal: "true" },
    esperado: quantos,
  }),

  comMedicaoSemAteste: (quantos: number | null): RecorteDaLista => ({
    rotulo: "Com medição aguardando ateste",
    parametros: { com_medicao_sem_ateste: "true" },
    esperado: quantos,
  }),

  semDefinicaoSegura: (quantos: number | null): RecorteDaLista => ({
    rotulo: "Vigência sem definição segura — nível D ou E, ou contestada",
    parametros: { ativos: "true", sem_definicao_segura: "true" },
    esperado: quantos,
  }),

  semProvidencia: (quantos: number | null): RecorteDaLista => ({
    rotulo: "A vencer e sem providência de continuidade concluída",
    parametros: { ativos: "true", sem_providencia: "true" },
    esperado: quantos,
  }),
}
